import { Component, Show } from "solid-js";
import { createSortable, transformStyle } from "@thisbeyond/solid-dnd";
import { Badge } from "../ui";
import { MovementBadge } from "./MovementBadge";
import { TypeIcon } from "../work-items/TypeIcon";
import { PriorityMeter } from "../work-items/PriorityMeter";
import { statusCategoryToVariant } from "../../utils/status";
import { formatEstimate } from "../../utils/estimate";
import type { WorkItem, WorkItemType, WorkItemStatus } from "../../api";
import { useProjectPrefix } from "../../contexts/ProjectContext";

export interface BacklogRowProps {
  item: WorkItem;
  itemType?: WorkItemType;
  statuses: WorkItemStatus[];
  parentTitle?: string;
  selected: boolean;
  dragEnabled: boolean;
  overlay?: boolean;
  /** How many positions the item moved in the prioritized backlog (positive = up, negative = down). */
  movement?: number;
  /** Human-readable explanation of why this item was placed at its current position. */
  reason?: string;
  onSelect: (id: string, selected: boolean, event: MouseEvent) => void;
  onClick: (item: WorkItem) => void;
}

const ROW_BASE =
  "group flex items-center h-8.25 px-6 border-b border-line " +
  "transition-colors duration-100 cursor-pointer " +
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-signal-cyan ";

const ID_CLASS =
  "font-mono text-xs tabular-nums text-ink-2 flex-shrink-0 w-16 mr-2";

/**
 * BacklogRow - Compact horizontal row for rendering work items in the backlog list.
 *
 * Layout (left to right):
 *   Drag handle | Checkbox | Type icon | Display ID | Title | Parent | Estimate | Priority | Status
 *
 * Supports drag-and-drop reordering via @thisbeyond/solid-dnd when dragEnabled is true.
 * When rendered as a drag overlay, shows a simplified version with just type icon, ID, and title.
 */
export const BacklogRow: Component<BacklogRowProps> = (props) => {
  const prefix = useProjectPrefix();

  // Only create sortable when drag is enabled and not in overlay mode
  const sortable =
    props.dragEnabled && !props.overlay
      ? createSortable(props.item.id)
      : undefined;

  const typeName = () =>
    props.item.item_type?.name ?? props.itemType?.name ?? "Task";

  const displayId = () => `${prefix()}-${props.item.item_number}`;

  const currentStatus = () =>
    props.statuses.find((s) => s.id === props.item.status_id);

  // The list API sends the estimate as flat columns; the nested `estimate`
  // object is never populated on this payload (FLOW-779).
  const estimate = () => {
    const v = props.item.estimate_value;
    const u = props.item.estimate_unit;
    return v != null && u ? formatEstimate(Number(v), u) : null;
  };

  const handleClick = (e: MouseEvent) => {
    // Don't navigate if clicking checkbox or drag handle
    if ((e.target as HTMLElement).closest("[data-no-row-click]")) return;
    props.onClick(props.item);
  };

  const handleCheckboxClick = (e: MouseEvent) => {
    e.stopPropagation();
    const newChecked = (e.target as HTMLInputElement).checked;
    props.onSelect(props.item.id, newChecked, e);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      if (!(e.target as HTMLElement).closest("[data-no-row-click]")) {
        e.preventDefault();
        props.onClick(props.item);
      }
    } else if (e.key === " ") {
      e.preventDefault();
      props.onSelect(
        props.item.id,
        !props.selected,
        e as unknown as MouseEvent,
      );
    }
  };

  // Overlay mode: simplified row for drag feedback
  if (props.overlay) {
    return (
      <div class="flex items-center h-8.25 px-6 bg-surface border border-ink rounded shadow-overlay">
        <div class="flex-shrink-0 mr-1.5">
          <TypeIcon typeName={typeName()} />
        </div>
        <span class={ID_CLASS}>
          <span class="font-normal">{prefix()}-</span>
          <span class="font-semibold">{props.item.item_number}</span>
        </span>
        <span class="text-sm text-ink truncate flex-1 min-w-0">
          {props.item.title}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={sortable?.ref}
      class={
        ROW_BASE +
        (sortable?.isActiveDraggable
          ? "opacity-40 bg-surface "
          : props.selected
            ? "bg-sunken "
            : "bg-surface hover:bg-paper ")
      }
      style={sortable ? transformStyle(sortable.transform) : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="row"
      tabindex="0"
      aria-selected={props.selected}
    >
      {/* Drag handle - reserves space always, icon visible on hover when drag enabled */}
      <div
        class="w-6 flex items-center justify-center flex-shrink-0 mr-1"
        data-no-row-click
        {...(sortable ? sortable.dragActivators : {})}
      >
        <Show when={props.dragEnabled}>
          <svg
            class="w-3.5 h-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity duration-100 cursor-grab"
            fill="currentColor"
            viewBox="0 0 16 16"
            aria-hidden="true"
          >
            <circle cx="5.5" cy="3.5" r="1.5" />
            <circle cx="10.5" cy="3.5" r="1.5" />
            <circle cx="5.5" cy="8" r="1.5" />
            <circle cx="10.5" cy="8" r="1.5" />
            <circle cx="5.5" cy="12.5" r="1.5" />
            <circle cx="10.5" cy="12.5" r="1.5" />
          </svg>
        </Show>
      </div>

      {/* Checkbox */}
      <div class="flex-shrink-0 mr-2" data-no-row-click>
        <label class="flex h-6.5 w-6.5 items-center justify-center cursor-pointer">
          <input
            type="checkbox"
            class={
              "w-3.5 h-3.5 rounded-sm border-line-strong accent-ink " +
              "cursor-pointer focus-ring " +
              (props.selected
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 transition-opacity duration-100")
            }
            checked={props.selected}
            onClick={handleCheckboxClick}
            aria-label={`Select ${displayId()}`}
          />
        </label>
      </div>

      {/* Type icon */}
      <div class="flex-shrink-0 mr-1.5">
        <TypeIcon typeName={typeName()} />
      </div>

      {/* Display ID */}
      <span data-display-id={displayId()} class={ID_CLASS}>
        <span class="font-normal">{prefix()}-</span>
        <span class="font-semibold">{props.item.item_number}</span>
      </span>

      {/* Title - takes remaining space */}
      <span class="text-sm text-ink truncate flex-1 min-w-0 mr-3">
        {props.item.title}
      </span>

      {/* Movement badge — shown when auto-prioritization has repositioned the item */}
      <Show when={props.movement !== undefined}>
        <span class="flex-shrink-0 mr-1.5">
          <MovementBadge delta={props.movement ?? 0} />
        </span>
      </Show>

      {/* Reason info icon — tooltip explaining the AI prioritization decision */}
      <Show when={props.reason}>
        <span
          class="flex-shrink-0 mr-2 text-ink-3 cursor-help leading-none"
          title={props.reason}
          aria-label={`Prioritization reason: ${props.reason}`}
          role="img"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </Show>

      {/* Parent breadcrumb (hidden below lg) */}
      <Show when={props.parentTitle}>
        <span
          class="hidden lg:inline-flex items-center gap-0.5 text-xs text-ink-3 truncate max-w-[140px] flex-shrink-0 mr-3"
          title={props.parentTitle}
        >
          <span aria-hidden="true" class="font-mono leading-none">
            ›
          </span>
          {props.parentTitle}
        </span>
      </Show>

      {/* Estimate (hidden below xl) */}
      <Show when={estimate()}>
        {(e) => (
          <span
            data-estimate
            class="hidden xl:inline-block w-14 text-right font-mono text-2xs tabular-nums text-ink-2 flex-shrink-0 mr-2"
          >
            {e()}
          </span>
        )}
      </Show>

      {/* Priority meter (hidden below md) */}
      <div class="hidden md:flex items-center justify-center w-5 flex-shrink-0 mr-2">
        <PriorityMeter priority={props.item.priority} />
      </div>

      {/* Status badge - always visible, rightmost */}
      <div class="w-28 flex-shrink-0 flex justify-start">
        <Show when={currentStatus()} keyed>
          {(status) => (
            <Badge
              variant={statusCategoryToVariant[status.category]}
              class="text-2xs px-1.5 whitespace-nowrap truncate"
            >
              {status.name}
            </Badge>
          )}
        </Show>
      </div>
    </div>
  );
};
